// src/components/kuestion/campus/ModeLocked.jsx
//
// The "mode already set" landing (contract v3 §9.0; build note §1). Copy
// follows the committed mode. A Solo account has no organisations row at all,
// so the organisation and prefix rows are not blank — they are not there.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useCampus } from './CampusContext';
import { LockIcon } from '../shared/Icons';
import { fmtDate } from '../shared/format';
import { kq } from '../../../config/routes';

export default function ModeLocked() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { modeState, modeInfo, org, students } = useCampus();
  const isCampus = modeState === 'campus';
  const chosen = modeInfo?.chosen_at ? fmtDate(modeInfo.chosen_at) : null;

  return (
    <div className="kqc kqc-wizard">
      <div className="kqc-topbar">
        <span className="kqc-topbar__brand">Account</span>
        <span className="kqc-spacer" />
        <span className="tag tag-neutral">{user?.email || user?.username}</span>
      </div>
      <div className="kqc-wizard__body" style={{ alignItems: 'center', justifyContent: 'flex-start' }}>
        <div className="kqc-wizard__inner" style={{ maxWidth: 640 }}>
          <span className="kqc-bigcheck kqc-bigcheck--sm"><LockIcon /></span>
          <div className="kqc-kicker" style={{ marginBottom: 12 }}>Account mode</div>
          <h1 className="kqc-h1--big" style={{ fontSize: 'clamp(28px,4.4vw,48px)', marginBottom: 14 }}>This account is {isCampus ? 'Campus' : 'Solo'}</h1>
          <p className="kqc-lede" style={{ fontSize: 15, lineHeight: 1.55, marginBottom: 24, maxWidth: '52ch' }}>
            {chosen ? `Chosen on ${chosen}. ` : ''}The mode is fixed for the life of the account, so there is no switch on this screen and no hidden one elsewhere.
          </p>

          <div className="kqc-box" style={{ marginBottom: 22 }}>
            <div className="kqc-box__head">Why it is fixed</div>
            <div className="kqc-box__body kqc-box__body--surface kqc-small" style={{ lineHeight: 1.55, color: 'var(--color-neutral-800)' }}>
              {isCampus
                ? 'A Campus account carries students, their credentials, their results and your cohort analytics. There is no safe automatic answer for what should happen to those if the account reverted to Solo — so rather than half-support a direction with real casualties, neither direction is supported.'
                : 'Turning a Solo account into an examiner account would be harmless in itself. It is closed anyway so that there is one rule to state and warn about rather than a direction-dependent one — the other direction has real casualties, and a mode that is permanent only some of the time is worse than one that always is.'}
            </div>
          </div>

          <div className="kqc-facts" style={{ marginBottom: 24 }}>
            <div><span>Mode</span><strong>{isCampus ? 'Campus' : 'Solo'} &middot; permanent</strong></div>
            <div><span>Students</span><strong>{isCampus ? `${students.length} in this organisation` : 'None — and none can be added'}</strong></div>
            {isCampus && org && (
              <>
                <div><span>Organisation</span><strong>{org.display_name}</strong></div>
                <div><span>Prefix</span><strong>{org.prefix} &middot; permanent</strong></div>
              </>
            )}
          </div>

          <div className="kqc-row">
            <button type="button" className="btn btn-primary" style={{ minHeight: 46 }} onClick={() => navigate(kq())}>Back to the dashboard</button>
            <a href="mailto:support@riftworks.tech" className="btn btn-secondary" style={{ minHeight: 46 }}>Contact support</a>
          </div>
          <p className="kqc-small kqc-muted" style={{ marginTop: 16, maxWidth: '52ch' }}>
            {isCampus
              ? 'Need Solo instead? Sign up again with a different email. Support can advise, but cannot convert an account in place.'
              : 'Need to run a class? Sign up again with a different email and choose Campus. Support can advise, but cannot convert an account in place.'}
          </p>
        </div>
      </div>
    </div>
  );
}
